import {
  ApplicationConfig,
  isDevMode,
  provideZoneChangeDetection,
  inject,
  provideAppInitializer,
  ErrorHandler,
  APP_INITIALIZER,
} from '@angular/core';
import { provideRouter, Router, withInMemoryScrolling } from '@angular/router';
import { appRoutes } from './app.routes';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { STATSIG_INIT_CONFIG, StatsigService } from '@statsig/angular-bindings';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  LanguageChangeService,
  authInterceptor,
  errorInterceptor,
  AppStorage,
  StatsigUserService,
  APP_BRAND,
  CookieService,
} from '@kuiper/ibusiness-core';

const DEFAULT_LANGUAGE = 'en';

export function preloadTranslation(transloco: TranslocoService) {
  return function () {
    const cookieService = inject(CookieService);
    const savedLang =
      cookieService.get(AppStorage.language) ?? DEFAULT_LANGUAGE;
    transloco.setActiveLang(savedLang);
    document.documentElement.dir = savedLang === 'ar' ? 'rtl' : 'ltr';
    return transloco.load(savedLang);
  };
}

export function initLanguageChangeListener() {
  return () => {
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(withInterceptors([errorInterceptor, authInterceptor])),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      })
    ),
    LanguageChangeService,
    provideTransloco({
      config: {
        availableLangs: ['ar', 'en'],
        defaultLang: DEFAULT_LANGUAGE,
        // When a key is missing in Arabic, use English
        fallbackLang: DEFAULT_LANGUAGE,
        missingHandler: {
          useFallbackTranslation: true,
        },
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(() => {
      const initializerFn = preloadTranslation(inject(TranslocoService));
      return initializerFn();
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: () => initLanguageChangeListener(),
      deps: [LanguageChangeService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: STATSIG_INIT_CONFIG,
      useFactory: () => StatsigUserService.getInitialConfig(),
    },
    provideAppInitializer(() => {
      const statsig = inject(StatsigService);
      return firstValueFrom(
        statsig.isLoading$.pipe(filter(loading => !loading))
      );
    }),
    {
      provide: APP_BRAND,
      useValue: 'dib',
    },
  ],
};
